import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { concat } from '@ember/helper';
import style from 'ember-style-modifier/modifiers/style';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency } from 'tio-ui/format';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import { not, and } from 'tio-ui/utilities';
import {
  PhRocket,
  PhCheckCircle,
  PhArrowRight,
  PhArrowLeft,
  PhBank,
  PhGraduationCap,
  PhBuildings,
  PhMagnifyingGlass,
  PhHandshake,
  PhSparkle,
  PhShieldCheck,
  PhTarget,
  PhChartLineUp,
  PhCurrencyDollar,
  PhBriefcase,
  PhWarning,
  PhInfo,
  PhSealCheck,
  PhConfetti,
  PhHeart,
  PhStar,
  PhLightning,
  PhPlugsConnected,
  PhCloudArrowUp,
  PhPencilSimple,
  PhQuestion,
  PhCaretRight,
  PhPlus,
  PhX,
} from 'ember-phosphor-icons';
import {
  getCurrentDemoClientData,
  getCurrentDemoScenario,
  getCurrentDemoClientId,
} from 'tio-employee/utils/career-pathways-demo-data';
import type CareerPathwaysApiService from 'tio-employee/services/career-pathways-api';
import type RouterService from '@ember/routing/router-service';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';

interface OnboardingSignature {
  Args: {
    model: unknown;
  };
}

type SummaryStatIntent = 'info' | 'success' | 'warning';

interface SummaryStatCardSignature {
  Args: {
    intent: SummaryStatIntent;
    label: string;
    value: string;
  };
}

// Co-located single-consumer card for the "loans connected" summary row —
// info/success/warning tinted stat tiles. Routes through the semantic soft
// surfaces so the tint, label, and value all follow the intent token.
const SummaryStatCard: TOC<SummaryStatCardSignature> = <template>
  <div
    class="rounded-xl p-4 border
      {{if
        (eq @intent 'info')
        'tio-bg-info-soft tio-border-info'
        (if
          (eq @intent 'success')
          'tio-bg-success-soft tio-border-success'
          'tio-bg-warning-soft tio-border-warning'
        )
      }}"
  >
    <Text
      @size="sm"
      class="font-medium
        {{if
          (eq @intent 'info')
          'tio-fg-info'
          (if (eq @intent 'success') 'tio-fg-success' 'tio-fg-warning')
        }}"
    >{{@label}}</Text>
    <Text
      class="tio-text-2xl-bold
        {{if
          (eq @intent 'info')
          'tio-fg-info-h'
          (if (eq @intent 'success') 'tio-fg-success-h' 'tio-fg-warning-h')
        }}"
    >{{@value}}</Text>
  </div>
</template>;

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: typeof PhRocket;
  status: 'completed' | 'current' | 'upcoming';
  optional?: boolean;
}

interface BenefitPreview {
  id: string;
  title: string;
  description: string;
  value: string;
  icon: typeof PhCurrencyDollar;
  color: string;
}

export default class OnboardingIndex extends Component<OnboardingSignature> {
  @service('career-pathways-api') declare careerPathwaysApi: CareerPathwaysApiService;
  @service declare router: RouterService;
  @service declare reimaginedData: ReimaginedDataService;

  @tracked currentStep = 1;
  @tracked hasLinkedLoans = false;
  @tracked hasCompletedPslfCheck = false;
  @tracked hasCompletedLrapDiscovery = false;
  @tracked hasSetGoal = false;

  // Loan connection flow state
  @tracked loanConnectionMethod: 'instant' | 'manual' | 'upload' | null = null;
  @tracked isConnecting = false;
  @tracked connectionProgress = 0;
  @tracked discoveredLoans: Array<{
    id: string;
    servicer: string;
    type: 'federal' | 'private';
    loanType: string;
    balance: number;
    interestRate: number;
    monthlyPayment: number;
    status: string;
  }> = [];
  @tracked showManualEntry = false;
  @tracked manualLoans: Array<{
    id: string;
    servicer: string;
    balance: string;
    interestRate: string;
    monthlyPayment: string;
    loanType: string;
  }> = [];
  @tracked showUploadOption = false;
  @tracked uploadedFile: string | null = null;
  @tracked hasPrivateLoans = false;

  // Demo: selected loan servicer
  @tracked selectedServicer: string = '';
  // Demo: PSLF check answers
  @tracked worksInPublicService: boolean | null = null;
  @tracked yearsInService = 0;
  // Demo: selected goal
  @tracked selectedGoal: string = '';

  get totalSteps() {
    return this._stepDefinitions.length;
  }

  get _stepDefinitions() {
    const steps: OnboardingStep[] = [
      {
        id: 'welcome',
        title: 'Welcome',
        description: 'Discover your benefits',
        icon: PhSparkle,
        status: 'upcoming' as const,
      },
    ];

    if (this.scenario.features.studentLoanRepayment) {
      steps.push({
        id: 'loans',
        title: 'Link Loans',
        description: 'Upload your loan documents',
        icon: PhBank,
        status: 'upcoming' as const,
      });
    }

    if (this.scenario.features.pslf) {
      steps.push({
        id: 'pslf',
        title: 'PSLF Check',
        description: 'Check forgiveness eligibility',
        icon: PhShieldCheck,
        status: 'upcoming' as const,
      });
    }

    if (this.showLrapStep) {
      steps.push({
        id: 'lrap',
        title: 'LRAP Discovery',
        description: 'Find assistance programs',
        icon: PhMagnifyingGlass,
        status: 'upcoming' as const,
        optional: true,
      });
    }

    steps.push({
      id: 'goals',
      title: 'Set Your Goal',
      description: 'Choose your path',
      icon: PhTarget,
      status: 'upcoming' as const,
    });

    return steps;
  }

  get steps(): OnboardingStep[] {
    return this._stepDefinitions.map((step, index) => {
      const stepNum = index + 1;
      return {
        ...step,
        status:
          this.currentStep > stepNum
            ? ('completed' as const)
            : this.currentStep === stepNum
              ? ('current' as const)
              : ('upcoming' as const),
      };
    });
  }
  // Inverse getters for template use (avoids unless/else lint error)
  get needsLoanLink() {
    return !this.hasLinkedLoans;
  }

  get needsPslfCheck() {
    return !this.hasCompletedPslfCheck;
  }

  get needsLrapDiscovery() {
    return !this.hasCompletedLrapDiscovery;
  }

  get needsGoal() {
    return !this.hasSetGoal;
  }

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get scenario() {
    return getCurrentDemoScenario();
  }

  get clientId() {
    return getCurrentDemoClientId();
  }

  get progressPercent() {
    return Math.round((this.currentStep / this.totalSteps) * 100);
  }

  get benefitPreviews(): BenefitPreview[] {
    const benefits: BenefitPreview[] = [];

    if (this.scenario.features.studentLoanRepayment) {
      benefits.push({
        id: 'slr',
        title: 'Student Loan Repayment',
        description: 'Tax-free employer contributions',
        value: formatCurrency(5250) + '/year',
        icon: PhHandshake,
        color: 'emerald',
      });
    }

    if (this.scenario.features.tuitionAssistance) {
      benefits.push({
        id: 'ta',
        title: 'Tuition Assistance',
        description: 'Advance your education',
        value: formatCurrency(5250) + '/year',
        icon: PhGraduationCap,
        color: 'violet',
      });
    }

    if (this.scenario.features.pslf) {
      benefits.push({
        id: 'pslf',
        title: 'PSLF Tracking',
        description: 'Track your forgiveness progress',
        value: 'Up to 100% forgiven',
        icon: PhShieldCheck,
        color: 'blue',
      });
    }

    benefits.push({
      id: 'tools',
      title: 'Financial Tools',
      description: 'Calculators & strategy finder',
      value: 'Always free',
      icon: PhChartLineUp,
      color: 'amber',
    });

    return benefits;
  }

  get showLrapStep() {
    return this.clientId === 'legal' || this.clientId === 'advocate';
  }

  // Loan servicers for demo
  get servicers() {
    return [
      { id: 'mohela', name: 'MOHELA', popular: true, logo: '🏛️' },
      { id: 'nelnet', name: 'Nelnet', popular: true, logo: '🏦' },
      { id: 'aidvantage', name: 'Aidvantage', popular: true, logo: '💰' },
      { id: 'edfinancial', name: 'EdFinancial', popular: false, logo: '📊' },
      { id: 'osla', name: 'OSLA', popular: false, logo: '🎓' },
      { id: 'ecsi', name: 'ECSI', popular: false, logo: '📋' },
    ];
  }

  get popularServicers() {
    return this.servicers.filter((s) => s.popular);
  }

  get totalDiscoveredBalance() {
    return this.discoveredLoans.reduce((sum, loan) => sum + loan.balance, 0);
  }

  get totalMonthlyPayment() {
    return this.discoveredLoans.reduce((sum, loan) => sum + loan.monthlyPayment, 0);
  }

  get avgInterestRate() {
    if (!this.discoveredLoans.length) return 0;
    const weighted = this.discoveredLoans.reduce(
      (sum, loan) => sum + loan.balance * loan.interestRate,
      0
    );
    return weighted / this.totalDiscoveredBalance;
  }

  get federalLoans() {
    return this.discoveredLoans.filter((loan) => loan.type === 'federal');
  }

  get privateLoans() {
    return this.discoveredLoans.filter((loan) => loan.type === 'private');
  }

  get unlockableValue() {
    // Calculate potential value user can unlock
    const section127 = 5250;
    const pslfPotential = this.clientId === 'advocate' ? 45000 : 0;
    return { section127, pslfPotential, total: section127 + (pslfPotential > 0 ? 50000 : 0) };
  }

  get hasPslfPotential() {
    return this.unlockableValue.pslfPotential >= 1;
  }

  get canRemoveManualLoan() {
    return this.manualLoans.length >= 2;
  }

  get hasManualLoans() {
    return this.manualLoans.length > 0;
  }

  get pslfEligibilityResult() {
    if (this.worksInPublicService === null) return null;
    if (!this.worksInPublicService) {
      return {
        eligible: false,
        message:
          'Your current employer may not qualify for PSLF. But you can still benefit from IDR plans and other forgiveness options.',
        suggestion: 'Use our Strategy Finder to explore your best repayment options.',
      };
    }
    return {
      eligible: true,
      estimatedPayments: Math.max(0, 120 - this.yearsInService * 12),
      estimatedForgiveness: 45000 + Math.random() * 30000,
      message: `Great news! You appear eligible for PSLF with approximately ${Math.max(0, 120 - this.yearsInService * 12)} payments remaining.`,
    };
  }

  get lrapDiscoveryResult() {
    if (!this.showLrapStep) return null;

    if (this.clientId === 'legal') {
      return {
        programsFound: 3,
        potentialValue: 17000,
        programs: [
          { name: 'Georgetown Law LRAP', value: 12000, type: 'school' },
          { name: 'DC Bar Foundation LRAP', value: 5000, type: 'state' },
          { name: 'Employer Section 127', value: 5250, type: 'employer' },
        ],
      };
    }

    if (this.clientId === 'advocate') {
      return {
        programsFound: 2,
        potentialValue: 42500,
        programs: [
          { name: 'NHSC Loan Repayment', value: 37500, type: 'federal' },
          { name: 'Illinois Nurse Educator LRAP', value: 5000, type: 'state' },
        ],
      };
    }

    return null;
  }

  get goalOptions() {
    const goals: Array<{
      id: string;
      title: string;
      description: string;
      icon: typeof PhStar;
      color: string;
    }> = [];

    if (this.scenario.features.studentLoanRepayment) {
      goals.push(
        {
          id: 'pay-off-fast',
          title: 'Pay Off Loans Fast',
          description: 'I want to eliminate my debt as quickly as possible',
          icon: PhLightning,
          color: 'amber',
        },
        {
          id: 'minimize-payments',
          title: 'Minimize Monthly Payments',
          description: 'I need lower payments to manage my budget',
          icon: PhCurrencyDollar,
          color: 'blue',
        }
      );

      if (this.scenario.features.pslf) {
        goals.push({
          id: 'maximize-forgiveness',
          title: 'Maximize Forgiveness',
          description: 'I want to minimize what I pay out-of-pocket through PSLF or IDR',
          icon: PhHeart,
          color: 'violet',
        });
      }
    }

    goals.push({
      id: 'advance-career',
      title: 'Advance My Career',
      description: 'I want to use education benefits to grow professionally',
      icon: PhStar,
      color: 'emerald',
    });

    return goals;
  }

  get currentStepId() {
    const step = this._stepDefinitions[this.currentStep - 1];
    return step?.id ?? 'welcome';
  }

  nextStep = () => {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
    }
  };

  previousStep = () => {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  };

  goToStep = (step: number) => {
    if (step <= this.currentStep + 1) {
      this.currentStep = step;
    }
  };

  // Step 2: Link loans
  selectServicer = (servicerId: string) => {
    this.selectedServicer = servicerId;
  };

  // Instant connection via FSV/Plaid-style API
  startInstantConnection = () => {
    // In live mode, navigate to the existing observability upload flow
    // which handles Plaid/Payitoff integration
    if (!this.reimaginedData.isDemoMode) {
      this.router.transitionTo('authenticated.observability.upload', {
        queryParams: {
          source: 'reimagined-onboarding',
          providers: this.reimaginedData.showPslf ? 'nslds,statement' : 'statement,nslds',
          returnTo: 'authenticated.reimagined.onboarding',
        },
      });
      return;
    }

    // Demo mode: simulate the connection flow
    this.loanConnectionMethod = 'instant';
    this.isConnecting = true;
    this.connectionProgress = 0;

    const progressInterval = setInterval(() => {
      this.connectionProgress += 15;
      if (this.connectionProgress >= 100) {
        clearInterval(progressInterval);
        this.completeInstantConnection();
      }
    }, 400);
  };

  completeInstantConnection = () => {
    this.discoveredLoans = [
      {
        id: '1',
        servicer: 'MOHELA',
        type: 'federal',
        loanType: 'Direct Unsubsidized',
        balance: 28420,
        interestRate: 5.5,
        monthlyPayment: 285,
        status: 'In Repayment',
      },
      {
        id: '2',
        servicer: 'MOHELA',
        type: 'federal',
        loanType: 'Direct Subsidized',
        balance: 18500,
        interestRate: 4.53,
        monthlyPayment: 165,
        status: 'In Repayment',
      },
      {
        id: '3',
        servicer: 'Nelnet',
        type: 'federal',
        loanType: 'Direct PLUS',
        balance: 21500,
        interestRate: 7.08,
        monthlyPayment: 248,
        status: 'In Repayment',
      },
    ];
    this.isConnecting = false;
    this.hasLinkedLoans = true;
  };

  // Manual entry flow
  startManualEntry = () => {
    this.loanConnectionMethod = 'manual';
    this.showManualEntry = true;
    // Add one empty loan to start
    this.addManualLoan();
  };

  addManualLoan = () => {
    this.manualLoans = [
      ...this.manualLoans,
      {
        id: `manual-${Date.now()}`,
        servicer: '',
        balance: '',
        interestRate: '',
        monthlyPayment: '',
        loanType: 'federal',
      },
    ];
  };

  removeManualLoan = (loanId: string) => {
    this.manualLoans = this.manualLoans.filter((loan) => loan.id !== loanId);
  };

  updateManualLoan = (loanId: string, field: string, value: string) => {
    this.manualLoans = this.manualLoans.map((loan) =>
      loan.id === loanId ? { ...loan, [field]: value } : loan
    );
  };

  submitManualLoans = () => {
    this.discoveredLoans = this.manualLoans
      .filter((loan) => loan.balance)
      .map((loan) => ({
        id: loan.id,
        servicer: loan.servicer || 'Unknown',
        type: (loan.loanType as 'federal' | 'private') || 'federal',
        loanType: 'Manual Entry',
        balance: parseFloat(loan.balance) || 0,
        interestRate: parseFloat(loan.interestRate) || 5.0,
        monthlyPayment: parseFloat(loan.monthlyPayment) || 0,
        status: 'Entered Manually',
      }));
    this.hasLinkedLoans = true;
    this.showManualEntry = false;
  };

  // Upload flow
  startUpload = () => {
    // In live mode, navigate to the observability upload flow for document upload
    if (!this.reimaginedData.isDemoMode) {
      this.router.transitionTo('authenticated.observability.upload', {
        queryParams: {
          source: 'reimagined-onboarding',
          providers: this.reimaginedData.showPslf ? 'nslds,statement' : 'statement',
          returnTo: 'authenticated.reimagined.onboarding',
        },
      });
      return;
    }

    // Demo mode: show upload UI
    this.loanConnectionMethod = 'upload';
    this.showUploadOption = true;
  };

  handleFileUpload = () => {
    // In live mode, the observability upload route handles this
    // This is only for demo mode simulation
    this.uploadedFile = 'loan_statement.pdf';
    // After upload, we'd process and discover loans
    setTimeout(() => {
      this.discoveredLoans = [
        {
          id: '1',
          servicer: 'From Document',
          type: 'federal',
          loanType: 'Direct Unsubsidized',
          balance: 35000,
          interestRate: 5.5,
          monthlyPayment: 350,
          status: 'Pending Verification',
        },
      ];
      this.hasLinkedLoans = true;
    }, 1500);
  };

  resetConnectionMethod = () => {
    this.loanConnectionMethod = null;
    this.showManualEntry = false;
    this.showUploadOption = false;
    this.isConnecting = false;
    this.connectionProgress = 0;
  };

  confirmLoanLink = () => {
    this.hasLinkedLoans = true;
    this.nextStep();
  };

  skipLoanLink = () => {
    this.nextStep();
  };

  // Step 3: PSLF check
  setPublicServiceAnswer = (answer: boolean) => {
    this.worksInPublicService = answer;
  };

  setYearsInService = (years: number) => {
    this.yearsInService = years;
  };

  completePslfCheck = () => {
    this.hasCompletedPslfCheck = true;
    // Skip LRAP step if not applicable - jump to goals step
    if (!this.showLrapStep) {
      const goalsIndex = this._stepDefinitions.findIndex((s) => s.id === 'goals');
      if (goalsIndex >= 0) {
        this.currentStep = goalsIndex + 1;
      } else {
        this.nextStep();
      }
    } else {
      this.nextStep();
    }
  };

  // Step 4: LRAP discovery
  completeLrapDiscovery = () => {
    this.hasCompletedLrapDiscovery = true;
    this.nextStep();
  };

  skipLrapDiscovery = () => {
    this.nextStep();
  };

  // Step 5: Set goal
  selectGoal = (goal: string) => {
    this.selectedGoal = goal;
  };

  completeOnboarding = () => {
    this.hasSetGoal = true;
    // In production: save preferences and navigate to dashboard
  };

  <template>
    {{! template-lint-disable no-bare-strings style-concatenation require-presentational-children }}
    <DataIndicator @source="demo" @showBadge={{true}} @dataSourceId="employee-profile">
      <VStack class="data-audit w-full min-w-0 max-w-4xl mx-auto">
        {{! Progress Header }}
        <Card @padding="lg" class="mb-6">
          <div class="tio-row-between mb-4">
            <div class="tio-row">
              <div class="p-2 tio-bg-info-soft rounded-lg">
                <PhRocket @size={{24}} class="tio-fg-info" />
              </div>
              <div>
                <Heading @level={{2}} @size="lg">
                  Getting Started
                </Heading>
                <Text @size="sm" class="tio-fg-muted">
                  Step
                  {{this.currentStep}}
                  of
                  {{this.totalSteps}}
                </Text>
              </div>
            </div>
            <div class="text-right">
              <Text @weight="bold" class="text-2xl tio-fg-info">{{this.progressPercent}}%</Text>
              <Text @size="sm" class="tio-fg-muted">Complete</Text>
            </div>
          </div>

          {{! Step indicators }}
          <div class="tio-row-tight">
            {{#each this.steps as |step index|}}
              <button
                type="button"
                class="flex-1 group"
                disabled={{eq step.status "upcoming"}}
                {{on
                  "click"
                  (fn
                    this.goToStep
                    (if
                      (eq index 0) 1 (if (eq index 1) 2 (if (eq index 2) 3 (if (eq index 3) 4 5)))
                    )
                  )
                }}
              >
                <div
                  class="h-2 rounded-full transition-all
                    {{if
                      (eq step.status 'completed')
                      'tio-bg-success'
                      (if (eq step.status 'current') 'tio-bg-info' 'tio-bg-track')
                    }}"
                ></div>
                <div class="mt-2 text-left">
                  <Text
                    @size="xs"
                    class="font-medium
                      {{if
                        (eq step.status 'current')
                        'tio-fg-info'
                        (if (eq step.status 'completed') 'tio-fg-success' 'tio-fg-disabled')
                      }}"
                  >
                    {{step.title}}
                  </Text>
                </div>
              </button>
            {{/each}}
          </div>
        </Card>

        {{! Step Content }}
        <Card @padding="none" class="p-8">
          {{! ── STEP 1: WELCOME ── }}
          {{#if (eq this.currentStepId "welcome")}}
            <div class="text-center mb-8">
              <div class="w-20 h-20 mx-auto mb-6 tio-gradient-brand rounded-2xl tio-row-center">
                <PhSparkle @size={{40}} class="tio-fg-inverse" @weight="fill" />
              </div>
              <Heading @level={{1}} class="font-bold mb-3">
                Welcome to
                {{this.demoClient.client.name}}
                Benefits!
              </Heading>
              <Text @size="lg" class="tio-fg-secondary max-w-xl mx-auto">
                You have access to powerful education and financial wellness benefits. Let's
                personalize your experience in just a few minutes.
              </Text>
            </div>

            {{! Benefit Cards }}
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {{#each this.benefitPreviews as |benefit|}}
                <div
                  class="p-5 rounded-xl border-2 transition-all
                    {{if
                      (eq benefit.color 'emerald')
                      'tio-border-success tio-bg-success-soft'
                      (if
                        (eq benefit.color 'violet')
                        'tio-border-brand-soft tio-bg-brand-wash'
                        (if
                          (eq benefit.color 'blue')
                          'tio-border-info tio-bg-info-soft'
                          'tio-border-warning tio-bg-warning-soft'
                        )
                      )
                    }}"
                >
                  <div class="tio-row-top-loose">
                    <div
                      class="p-3 rounded-lg
                        {{if
                          (eq benefit.color 'emerald')
                          'tio-bg-success-soft tio-fg-success'
                          (if
                            (eq benefit.color 'violet')
                            'tio-bg-brand-soft tio-fg-brand'
                            (if
                              (eq benefit.color 'blue')
                              'tio-bg-info-soft tio-fg-info'
                              'tio-bg-warning-soft tio-fg-warning'
                            )
                          )
                        }}"
                    >
                      <benefit.icon @size={{24}} />
                    </div>
                    <div class="flex-1">
                      <Text @weight="semibold">{{benefit.title}}</Text>
                      <Text @size="sm" class="tio-fg-secondary">{{benefit.description}}</Text>
                      <Text
                        @size="lg"
                        class="font-bold mt-1
                          {{if
                            (eq benefit.color 'emerald')
                            'tio-fg-success-body'
                            (if
                              (eq benefit.color 'violet')
                              'tio-fg-brand'
                              (if
                                (eq benefit.color 'blue') 'tio-fg-info-body' 'tio-fg-warning-body'
                              )
                            )
                          }}"
                      >
                        {{benefit.value}}
                      </Text>
                    </div>
                  </div>
                </div>
              {{/each}}
            </div>

            <div class="tio-flex-center">
              <Button
                @intent="primary"
                @size="lg"
                @icon={{PhArrowRight}}
                @iconPlacement="end"
                @label="Let's Get Started"
                {{on "click" this.nextStep}}
              />
            </div>
          {{/if}}

          {{! ── STEP 2: LINK LOANS (REIMAGINED) ── }}
          {{#if (eq this.currentStepId "loans")}}
            {{#if this.hasLinkedLoans}}
              {{! SUCCESS STATE: Loans discovered }}
              <div class="mb-6">
                <div class="tio-row mb-2">
                  <div class="p-2 tio-bg-success-soft rounded-lg">
                    <PhCheckCircle @size={{28}} class="tio-fg-success" />
                  </div>
                  <div>
                    <Heading @level={{2}} class="font-bold">
                      Loans Connected Successfully!
                    </Heading>
                    <Text class="tio-fg-secondary">
                      We found
                      {{this.discoveredLoans.length}}
                      loans. Here's what we discovered:
                    </Text>
                  </div>
                </div>
              </div>

              {{! Summary Cards }}
              <div class="grid grid-cols-3 gap-4 mb-6">
                <SummaryStatCard
                  @intent="info"
                  @label="Total Balance"
                  @value={{formatCurrency this.totalDiscoveredBalance}}
                />
                <SummaryStatCard
                  @intent="success"
                  @label="Monthly Payment"
                  @value="{{formatCurrency this.totalMonthlyPayment}}/mo"
                />
                <SummaryStatCard
                  @intent="warning"
                  @label="Avg. Interest"
                  @value="{{this.avgInterestRate}}%"
                />
              </div>

              {{! Discovered Loans List }}
              <Card @padding="none" class="overflow-hidden mb-6">
                <div class="px-4 py-3 tio-bg-sunken border-b tio-border-default">
                  <Text @weight="medium" class="tio-fg-secondary">Your Student Loans</Text>
                </div>
                <div class="divide-y tio-divide-subtle">
                  {{#each this.discoveredLoans as |loan|}}
                    <div class="p-4 tio-row-loose">
                      <div
                        class="p-2 rounded-lg
                          {{if (eq loan.type 'federal') 'tio-bg-info-soft' 'tio-bg-brand-soft'}}"
                      >
                        <PhBank
                          @size={{20}}
                          class={{if (eq loan.type "federal") "tio-fg-info" "tio-fg-brand"}}
                        />
                      </div>
                      <div class="tio-fill-min">
                        <div class="tio-row-tight">
                          <Text @weight="medium">{{loan.loanType}}</Text>
                          <Chip
                            @intent={{if (eq loan.type "federal") "primary" "default"}}
                            @size="sm"
                          >
                            {{loan.type}}
                          </Chip>
                        </div>
                        <Text @size="sm" class="tio-fg-muted">{{loan.servicer}}
                          ·
                          {{loan.status}}</Text>
                      </div>
                      <div class="text-right">
                        <Text @weight="semibold">{{formatCurrency loan.balance}}</Text>
                        <Text @size="sm" class="tio-fg-muted">{{loan.interestRate}}% ·
                          {{formatCurrency loan.monthlyPayment}}/mo</Text>
                      </div>
                    </div>
                  {{/each}}
                </div>
              </Card>

              {{! What You've Unlocked }}
              <div class="tio-bg-success-soft rounded-xl p-5 border tio-border-success mb-6">
                <div class="tio-row-top-loose">
                  <div class="p-2 tio-bg-success-soft rounded-lg">
                    <PhSparkle @size={{24}} class="tio-fg-success" />
                  </div>
                  <div>
                    <Text @weight="semibold" class="tio-fg-success-h">You've Unlocked These
                      Benefits!</Text>
                    <div class="mt-3 grid grid-cols-2 gap-3">
                      <div class="tio-row-tight">
                        <PhCheckCircle @size={{18}} class="tio-fg-success" />
                        <Text @size="sm" class="tio-fg-success-h">Employer payments toward your
                          loans</Text>
                      </div>
                      <div class="tio-row-tight">
                        <PhCheckCircle @size={{18}} class="tio-fg-success" />
                        <Text @size="sm" class="tio-fg-success-h">Personalized repayment strategy</Text>
                      </div>
                      <div class="tio-row-tight">
                        <PhCheckCircle @size={{18}} class="tio-fg-success" />
                        <Text @size="sm" class="tio-fg-success-h">PSLF eligibility analysis</Text>
                      </div>
                      <div class="tio-row-tight">
                        <PhCheckCircle @size={{18}} class="tio-fg-success" />
                        <Text @size="sm" class="tio-fg-success-h">IDR plan comparison</Text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            {{else if this.isConnecting}}
              {{! CONNECTING STATE: Progress animation }}
              <div class="text-center py-12">
                <div
                  class="w-20 h-20 mx-auto mb-6 tio-bg-info-soft rounded-full tio-row-center relative"
                >
                  <PhPlugsConnected @size={{40}} class="tio-fg-info" />
                  <div
                    class="tio-onboarding-connecting-spinner absolute inset-0 rounded-full border-4 tio-border-info animate-spin"
                  ></div>
                </div>
                <Heading @level={{2}} @size="xl" class="font-bold mb-2">
                  Securely Connecting...
                </Heading>
                <Text class="tio-fg-secondary mb-6">
                  We're pulling your loan information from the federal database.
                </Text>

                {{! Progress bar }}
                <div class="max-w-sm mx-auto">
                  <div class="h-2 tio-bg-track rounded-full overflow-hidden">
                    <div
                      class="h-full tio-bg-brand rounded-full transition-all duration-300"
                      {{style width=(concat this.connectionProgress "%")}}
                    ></div>
                  </div>
                  <div class="tio-flex-between mt-2 text-sm tio-fg-muted">
                    <span>Authenticating</span>
                    <span>{{this.connectionProgress}}%</span>
                  </div>
                </div>

                <div class="mt-8 p-4 tio-bg-info-soft rounded-lg max-w-md mx-auto">
                  <div class="tio-row-top">
                    <PhShieldCheck @size={{20}} class="tio-fg-info shrink-0 mt-0.5" />
                    <Text @size="sm" class="tio-fg-info-h text-left">
                      Your credentials are never stored. We use bank-level encryption and read-only
                      access.
                    </Text>
                  </div>
                </div>
              </div>

            {{else if this.showManualEntry}}
              {{! MANUAL ENTRY STATE }}
              <div class="mb-6">
                <div class="tio-row-between mb-4">
                  <div class="tio-row">
                    <button
                      type="button"
                      class="p-1 hover:tio-bg-sunken rounded"
                      {{on "click" this.resetConnectionMethod}}
                    >
                      <PhArrowLeft @size={{20}} class="tio-fg-muted" />
                    </button>
                    <div>
                      <Heading @level={{2}} @size="xl" class="font-bold">
                        Enter Your Loans Manually
                      </Heading>
                      <Text @size="sm" class="tio-fg-secondary">
                        Add each loan you want to track
                      </Text>
                    </div>
                  </div>
                </div>

                <div class="space-y-4 mb-6">
                  {{#each this.manualLoans as |loan index|}}
                    <div class="tio-bg-sunken rounded-xl p-4 border tio-border-default">
                      <div class="tio-row-between mb-3">
                        <Text @weight="medium" class="tio-fg-secondary">Loan #{{index}}</Text>
                        {{#if this.canRemoveManualLoan}}
                          <button
                            type="button"
                            class="p-1 hover:tio-bg-sunken-hover rounded tio-fg-disabled hover:tio-fg-danger"
                            {{on "click" (fn this.removeManualLoan loan.id)}}
                          >
                            <PhX @size={{16}} />
                          </button>
                        {{/if}}
                      </div>
                      <div class="grid grid-cols-2 gap-3">
                        <div>
                          <label class="block text-xs tio-fg-muted mb-1">Servicer</label>
                          <select
                            class="w-full px-3 py-2 border tio-border-strong rounded-lg text-sm focus:ring-2 focus:tio-ring-info focus:tio-border-info"
                          >
                            <option value="">Select servicer...</option>
                            {{#each this.servicers as |servicer|}}
                              <option value={{servicer.id}}>{{servicer.name}}</option>
                            {{/each}}
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label class="block text-xs tio-fg-muted mb-1">Loan Type</label>
                          <select
                            class="w-full px-3 py-2 border tio-border-strong rounded-lg text-sm focus:ring-2 focus:tio-ring-info focus:tio-border-info"
                          >
                            <option value="federal">Federal</option>
                            <option value="private">Private</option>
                          </select>
                        </div>
                        <div>
                          <label class="block text-xs tio-fg-muted mb-1">Balance</label>
                          <div class="relative">
                            <span
                              class="absolute left-3 top-1/2 -translate-y-1/2 tio-fg-disabled"
                            >$</span>
                            <input
                              type="number"
                              placeholder="0"
                              class="w-full pl-7 pr-3 py-2 border tio-border-strong rounded-lg text-sm focus:ring-2 focus:tio-ring-info focus:tio-border-info"
                            />
                          </div>
                        </div>
                        <div>
                          <label class="block text-xs tio-fg-muted mb-1">Interest Rate</label>
                          <div class="relative">
                            <input
                              type="number"
                              step="0.01"
                              placeholder="5.0"
                              class="w-full pr-7 pl-3 py-2 border tio-border-strong rounded-lg text-sm focus:ring-2 focus:tio-ring-info focus:tio-border-info"
                            />
                            <span
                              class="absolute right-3 top-1/2 -translate-y-1/2 tio-fg-disabled"
                            >%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  {{/each}}
                </div>

                <button
                  type="button"
                  class="w-full py-3 border-2 border-dashed tio-border-strong rounded-xl tio-fg-muted hover:tio-border-info hover:tio-fg-info hover:tio-bg-info-soft transition-all tio-row-tight justify-center"
                  {{on "click" this.addManualLoan}}
                >
                  <PhPlus @size={{18}} />
                  Add Another Loan
                </button>

                <div class="mt-4 p-4 tio-bg-warning-soft rounded-lg border tio-border-warning">
                  <div class="tio-row-top">
                    <PhInfo @size={{18}} class="tio-fg-warning shrink-0 mt-0.5" />
                    <Text @size="sm" class="tio-fg-warning-h">
                      Tip: You can find this information on your loan servicer's website, your
                      latest statement, or by downloading your NSLDS file from StudentAid.gov.
                    </Text>
                  </div>
                </div>
              </div>

            {{else if this.showUploadOption}}
              {{! UPLOAD STATE }}
              <div class="mb-6">
                <div class="tio-row mb-4">
                  <button
                    type="button"
                    class="p-1 hover:tio-bg-sunken rounded"
                    {{on "click" this.resetConnectionMethod}}
                  >
                    <PhArrowLeft @size={{20}} class="tio-fg-muted" />
                  </button>
                  <div>
                    <Heading @level={{2}} @size="xl" class="font-bold">
                      Upload a Document
                    </Heading>
                    <Text @size="sm" class="tio-fg-secondary">
                      We'll extract your loan information automatically
                    </Text>
                  </div>
                </div>

                <div
                  class="border-2 border-dashed tio-border-strong rounded-xl p-12 text-center hover:tio-border-info hover:tio-bg-info-soft transition-all cursor-pointer"
                  role="button"
                  {{on "click" this.handleFileUpload}}
                >
                  <PhCloudArrowUp @size={{48}} class="mx-auto tio-fg-disabled mb-4" />
                  <Text @weight="medium" class="tio-fg-secondary mb-2">
                    Drop your file here, or click to browse
                  </Text>
                  <Text @size="sm" class="tio-fg-muted">
                    Accepts PDF, PNG, or JPEG up to 10MB
                  </Text>
                </div>

                <div class="mt-6 space-y-3">
                  <Text @size="sm" @weight="medium" class="tio-fg-secondary">What documents work
                    best:</Text>
                  <div class="grid grid-cols-2 gap-3">
                    <div class="tio-row-top-tight p-3 tio-surface-sunken">
                      <PhCheckCircle @size={{16}} class="tio-fg-success shrink-0 mt-0.5" />
                      <Text @size="sm" class="tio-fg-secondary">Recent loan statement</Text>
                    </div>
                    <div class="tio-row-top-tight p-3 tio-surface-sunken">
                      <PhCheckCircle @size={{16}} class="tio-fg-success shrink-0 mt-0.5" />
                      <Text @size="sm" class="tio-fg-secondary">NSLDS report screenshot</Text>
                    </div>
                    <div class="tio-row-top-tight p-3 tio-surface-sunken">
                      <PhCheckCircle @size={{16}} class="tio-fg-success shrink-0 mt-0.5" />
                      <Text @size="sm" class="tio-fg-secondary">StudentAid.gov download</Text>
                    </div>
                    <div class="tio-row-top-tight p-3 tio-surface-sunken">
                      <PhCheckCircle @size={{16}} class="tio-fg-success shrink-0 mt-0.5" />
                      <Text @size="sm" class="tio-fg-secondary">Servicer account summary</Text>
                    </div>
                  </div>
                </div>
              </div>

            {{else}}
              {{! INITIAL STATE: Choose connection method }}
              <div class="mb-8">
                <div class="tio-row mb-2">
                  <PhCloudArrowUp @size={{28}} class="tio-fg-info" />
                  <Heading @level={{2}} class="font-bold">
                    Upload Your Student Loan Documents
                  </Heading>
                </div>
                <Text class="tio-fg-secondary">
                  Uploading your loan documents unlocks employer contributions, forgiveness
                  tracking, and personalized strategies.
                </Text>
              </div>

              {{! Value Preview }}
              <div class="tio-bg-brand-wash rounded-xl p-5 border tio-border-brand-soft mb-8">
                <div class="tio-row-top-loose">
                  <div class="p-2 tio-bg-brand-soft rounded-lg">
                    <PhSparkle @size={{24}} class="tio-fg-brand" />
                  </div>
                  <div>
                    <Text @weight="semibold" class="tio-fg-brand">Connect to unlock up to
                      {{formatCurrency this.unlockableValue.total}}
                      in benefits</Text>
                    <div class="tio-wrap-loose mt-2">
                      <div class="tio-row-tight">
                        <PhHandshake @size={{16}} class="tio-fg-brand" />
                        <Text @size="sm" class="tio-fg-brand">{{formatCurrency
                            this.unlockableValue.section127
                          }}/year employer payments</Text>
                      </div>
                      {{#if this.hasPslfPotential}}
                        <div class="tio-row-tight">
                          <PhShieldCheck @size={{16}} class="tio-fg-brand" />
                          <Text @size="sm" class="tio-fg-brand">PSLF forgiveness tracking</Text>
                        </div>
                      {{/if}}
                    </div>
                  </div>
                </div>
              </div>

              {{! Connection Methods }}
              <div class="space-y-4">
                {{! PRIMARY: Instant Connect }}
                <button
                  type="button"
                  class="w-full p-5 border-2 tio-border-info tio-bg-info-soft rounded-xl text-left transition-all hover:tio-bg-info-soft group"
                  {{on "click" this.startInstantConnection}}
                >
                  <div class="tio-row-top-loose">
                    <div
                      class="tio-onboarding-instant-icon p-3 tio-bg-info-soft rounded-lg transition-colors"
                    >
                      <PhPlugsConnected @size={{28}} class="tio-fg-info" />
                    </div>
                    <div class="flex-1">
                      <div class="tio-row-tight">
                        <Text @size="lg" @weight="semibold">Connect Instantly</Text>
                        <Chip @intent="success" @size="sm">Recommended</Chip>
                      </div>
                      <Text class="tio-fg-secondary mt-1">
                        Securely link to StudentAid.gov in seconds. No uploading required.
                      </Text>
                      <div class="tio-row-loose mt-3 text-sm">
                        <div class="tio-row-extra-tight tio-fg-success">
                          <PhShieldCheck @size={{16}} />
                          <span>Bank-level security</span>
                        </div>
                        <div class="tio-row-extra-tight tio-fg-success">
                          <PhLightning @size={{16}} />
                          <span>30 seconds</span>
                        </div>
                      </div>
                    </div>
                    <PhCaretRight @size={{24}} class="tio-fg-muted group-hover:tio-fg-info" />
                  </div>
                </button>

                {{! SECONDARY: Manual Entry }}
                <button
                  type="button"
                  class="w-full p-5 border-2 tio-border-default rounded-xl text-left transition-all hover:tio-border-strong hover:tio-bg-sunken group"
                  {{on "click" this.startManualEntry}}
                >
                  <div class="tio-row-top-loose">
                    <div class="tio-onboarding-option-icon transition-colors">
                      <PhPencilSimple @size={{28}} class="tio-fg-secondary" />
                    </div>
                    <div class="flex-1">
                      <Text @size="lg" @weight="semibold">Enter Manually</Text>
                      <Text class="tio-fg-secondary mt-1">
                        Type in your loan details if you have them handy.
                      </Text>
                    </div>
                    <PhCaretRight
                      @size={{24}}
                      class="tio-fg-disabled group-hover:tio-fg-secondary"
                    />
                  </div>
                </button>

                {{! TERTIARY: Upload }}
                <button
                  type="button"
                  class="w-full p-5 border-2 tio-border-default rounded-xl text-left transition-all hover:tio-border-strong hover:tio-bg-sunken group"
                  {{on "click" this.startUpload}}
                >
                  <div class="tio-row-top-loose">
                    <div class="tio-onboarding-option-icon transition-colors">
                      <PhCloudArrowUp @size={{28}} class="tio-fg-secondary" />
                    </div>
                    <div class="flex-1">
                      <Text @size="lg" @weight="semibold">Upload a Statement</Text>
                      <Text class="tio-fg-secondary mt-1">
                        We'll extract loan details from your document.
                      </Text>
                    </div>
                    <PhCaretRight
                      @size={{24}}
                      class="tio-fg-disabled group-hover:tio-fg-secondary"
                    />
                  </div>
                </button>
              </div>

              {{! Help Link }}
              <div class="mt-6 text-center">
                <button
                  type="button"
                  class="tio-inline-row gap-2 text-sm tio-fg-muted hover:tio-fg-info"
                >
                  <PhQuestion @size={{16}} />
                  <span>Not sure where to find your loan info?</span>
                </button>
              </div>
            {{/if}}

            <div class="tio-flex-between mt-8 pt-6 border-t tio-border-default">
              <Button
                @intent="default"
                @appearance="outlined"
                @icon={{PhArrowLeft}}
                @label="Back"
                {{on "click" this.previousStep}}
              />
              <div class="tio-flex">
                {{#if (and this.needsLoanLink (not this.isConnecting))}}
                  <Button
                    @intent="default"
                    @appearance="outlined"
                    @label="Skip for now"
                    {{on "click" this.skipLoanLink}}
                  />
                {{/if}}
                {{#if this.hasLinkedLoans}}
                  <Button
                    @intent="primary"
                    @icon={{PhArrowRight}}
                    @iconPlacement="end"
                    @label="Continue"
                    {{on "click" this.nextStep}}
                  />
                {{else if this.showManualEntry}}
                  <Button
                    @intent="primary"
                    @icon={{PhArrowRight}}
                    @iconPlacement="end"
                    @label="Save Loans"
                    disabled={{not this.hasManualLoans}}
                    {{on "click" this.submitManualLoans}}
                  />
                {{/if}}
              </div>
            </div>
          {{/if}}

          {{! ── STEP 3: PSLF CHECK ── }}
          {{#if (eq this.currentStepId "pslf")}}
            <div class="mb-8">
              <div class="tio-row mb-2">
                <PhShieldCheck @size={{28}} class="tio-fg-info" />
                <Heading @level={{2}} class="font-bold">
                  Public Service Loan Forgiveness Check
                </Heading>
              </div>
              <Text class="tio-fg-secondary">
                PSLF can forgive your remaining federal loans after 120 qualifying payments. Let's
                see if you qualify.
              </Text>
            </div>

            {{#if this.hasCompletedPslfCheck}}
              <div class="p-6 tio-bg-success-soft border tio-border-success rounded-xl">
                <div class="tio-row-loose">
                  <div class="p-3 tio-bg-success-soft rounded-full">
                    <PhCheckCircle @size={{32}} class="tio-fg-success" />
                  </div>
                  <div>
                    <Text @size="lg" @weight="semibold" class="tio-fg-success-h">PSLF Check
                      Complete!</Text>
                    <Text class="tio-fg-success-body">
                      We've saved your eligibility status and will track your progress.
                    </Text>
                  </div>
                </div>
              </div>
            {{else}}
              <div class="space-y-6">
                <div>
                  <Text @weight="medium" class="tio-fg-secondary mb-3">
                    Do you work for a qualifying public service employer?
                  </Text>
                  <Text @size="sm" class="tio-fg-muted mb-4">
                    This includes government (federal, state, local), 501(c)(3) nonprofits, tribal
                    organizations, and some AmeriCorps/Peace Corps positions.
                  </Text>

                  <div class="tio-flex-loose">
                    <button
                      type="button"
                      class="flex-1 p-5 border-2 rounded-xl transition-all
                        {{if
                          (eq this.worksInPublicService true)
                          'tio-border-success tio-bg-success-soft'
                          'tio-border-default hover:tio-border-success'
                        }}"
                      {{on "click" (fn this.setPublicServiceAnswer true)}}
                    >
                      <div class="text-center">
                        <PhBuildings
                          @size={{32}}
                          class={{if
                            (eq this.worksInPublicService true)
                            "tio-fg-success"
                            "tio-fg-disabled"
                          }}
                        />
                        <Text @weight="medium" class="mt-2">Yes</Text>
                      </div>
                    </button>
                    <button
                      type="button"
                      class="flex-1 p-5 border-2 rounded-xl transition-all
                        {{if
                          (eq this.worksInPublicService false)
                          'tio-border-strong tio-bg-sunken'
                          'tio-border-default hover:tio-border-strong'
                        }}"
                      {{on "click" (fn this.setPublicServiceAnswer false)}}
                    >
                      <div class="text-center">
                        <PhBriefcase
                          @size={{32}}
                          class={{if
                            (eq this.worksInPublicService false)
                            "tio-fg-secondary"
                            "tio-fg-disabled"
                          }}
                        />
                        <Text @weight="medium" class="mt-2">No / Not Sure</Text>
                      </div>
                    </button>
                  </div>
                </div>

                {{#if (eq this.worksInPublicService true)}}
                  <div class="p-5 tio-bg-info-soft border tio-border-info rounded-xl">
                    <Text @weight="medium" class="tio-fg-info-h mb-3">
                      How many years have you worked in public service?
                    </Text>
                    <div class="tio-wrap-tight">
                      {{#each (array 0 1 2 3 4 5 6 7 8 9 10) as |years|}}
                        <button
                          type="button"
                          class="px-4 py-2 border-2 rounded-lg transition-all
                            {{if
                              (eq this.yearsInService years)
                              'tio-border-info tio-bg-info-soft tio-fg-info-body'
                              'tio-border-default tio-bg-surface hover:tio-border-info'
                            }}"
                          {{on "click" (fn this.setYearsInService years)}}
                        >
                          {{#if (eq years 10)}}10+{{else}}{{years}}{{/if}}
                        </button>
                      {{/each}}
                    </div>
                  </div>
                {{/if}}

                {{! PSLF Result }}
                {{#if this.pslfEligibilityResult}}
                  <div
                    class="p-5 rounded-xl border-2
                      {{if
                        this.pslfEligibilityResult.eligible
                        'tio-bg-success-soft tio-border-success'
                        'tio-bg-warning-soft tio-border-warning'
                      }}"
                  >
                    <div class="tio-row-top-loose">
                      {{#if this.pslfEligibilityResult.eligible}}
                        <PhSealCheck @size={{32}} class="tio-fg-success shrink-0" />
                      {{else}}
                        <PhInfo @size={{32}} class="tio-fg-warning shrink-0" />
                      {{/if}}
                      <div>
                        <Text
                          @size="lg"
                          class="font-semibold
                            {{if
                              this.pslfEligibilityResult.eligible
                              'tio-fg-success-h'
                              'tio-fg-warning-h'
                            }}"
                        >
                          {{#if this.pslfEligibilityResult.eligible}}
                            You May Qualify for PSLF!
                          {{else}}
                            PSLF May Not Apply, But You Have Options
                          {{/if}}
                        </Text>
                        <Text
                          class="mt-1
                            {{if
                              this.pslfEligibilityResult.eligible
                              'tio-fg-success-body'
                              'tio-fg-warning-body'
                            }}"
                        >
                          {{this.pslfEligibilityResult.message}}
                        </Text>
                        {{#if this.pslfEligibilityResult.eligible}}
                          <div class="tio-onboarding-eligibility-summary">
                            <div>
                              <Text @size="sm" class="tio-fg-success-body">Payments Remaining</Text>
                              <Text @weight="bold" class="text-2xl tio-fg-success-h">
                                ~{{this.pslfEligibilityResult.estimatedPayments}}
                              </Text>
                            </div>
                            <div>
                              <Text @size="sm" class="tio-fg-success-body">Estimated Forgiveness</Text>
                              <Text @weight="bold" class="text-2xl tio-fg-success-h">
                                {{formatCurrency this.pslfEligibilityResult.estimatedForgiveness}}
                              </Text>
                            </div>
                          </div>
                        {{/if}}
                      </div>
                    </div>
                  </div>
                {{/if}}
              </div>
            {{/if}}

            <div class="tio-flex-between mt-8 pt-6 border-t tio-border-default">
              <Button
                @intent="default"
                @appearance="outlined"
                @icon={{PhArrowLeft}}
                @label="Back"
                {{on "click" this.previousStep}}
              />
              <Button
                @intent="primary"
                @icon={{PhArrowRight}}
                @iconPlacement="end"
                @label="Continue"
                disabled={{eq this.worksInPublicService null}}
                {{on "click" this.completePslfCheck}}
              />
            </div>
          {{/if}}

          {{! ── STEP 4: LRAP DISCOVERY ── }}
          {{#if (eq this.currentStepId "lrap")}}
            <div class="mb-8">
              <div class="tio-row mb-2">
                <PhMagnifyingGlass @size={{28}} class="tio-fg-brand" />
                <Heading @level={{2}} class="font-bold">
                  Loan Repayment Assistance Discovery
                </Heading>
              </div>
              <Text class="tio-fg-secondary">
                Based on your employer and profile, you may qualify for additional assistance
                programs beyond your employer benefits.
              </Text>
            </div>

            {{#if this.hasCompletedLrapDiscovery}}
              <div class="p-6 tio-bg-success-soft border tio-border-success rounded-xl">
                <div class="tio-row-loose">
                  <div class="p-3 tio-bg-success-soft rounded-full">
                    <PhCheckCircle @size={{32}} class="tio-fg-success" />
                  </div>
                  <div>
                    <Text @size="lg" @weight="semibold" class="tio-fg-success-h">Discovery Complete!</Text>
                    <Text class="tio-fg-success-body">
                      Visit LRAP Discovery anytime to explore details and apply.
                    </Text>
                  </div>
                </div>
              </div>
            {{else}}
              {{#if this.lrapDiscoveryResult}}
                <div class="p-6 tio-bg-brand-wash border tio-border-brand-soft rounded-xl mb-6">
                  <div class="tio-row-top-loose">
                    <div class="p-3 tio-bg-brand-soft rounded-full shrink-0">
                      <PhSparkle @size={{28}} class="tio-fg-brand" />
                    </div>
                    <div class="flex-1">
                      <Text @size="lg" @weight="semibold" class="tio-fg-brand">
                        We Found
                        {{this.lrapDiscoveryResult.programsFound}}
                        Programs!
                      </Text>
                      <Text class="tio-fg-brand mt-1">
                        You may qualify for up to
                        <strong>
                          {{formatCurrency this.lrapDiscoveryResult.potentialValue}}/year
                        </strong>
                        in additional assistance.
                      </Text>

                      <div class="mt-4 space-y-3">
                        {{#each this.lrapDiscoveryResult.programs as |program|}}
                          <div class="tio-row-between p-3 tio-bg-surface rounded-lg">
                            <div class="tio-row">
                              {{#if (eq program.type "school")}}
                                <PhGraduationCap @size={{20}} class="tio-fg-brand-accent" />
                              {{else if (eq program.type "state")}}
                                <PhBuildings @size={{20}} class="tio-fg-info" />
                              {{else if (eq program.type "federal")}}
                                <PhShieldCheck @size={{20}} class="tio-fg-success" />
                              {{else}}
                                <PhHandshake @size={{20}} class="tio-fg-warning" />
                              {{/if}}
                              <div>
                                <Text @weight="medium">{{program.name}}</Text>
                                <Text
                                  @size="sm"
                                  class="tio-fg-muted capitalize"
                                >{{program.type}}</Text>
                              </div>
                            </div>
                            <Text @weight="bold" class="tio-fg-brand">
                              {{formatCurrency program.value}}/yr
                            </Text>
                          </div>
                        {{/each}}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="p-4 tio-bg-warning-soft border tio-border-warning rounded-xl mb-6">
                  <div class="tio-row-top">
                    <PhWarning @size={{20}} class="tio-fg-warning shrink-0 mt-0.5" />
                    <Text @size="sm" class="tio-fg-warning-h">
                      <strong>Stacking Note:</strong>
                      Some programs have "outside funds" policies that may affect how benefits
                      combine. We'll help you optimize your strategy to maximize total assistance.
                    </Text>
                  </div>
                </div>
              {{/if}}
            {{/if}}

            <div class="tio-flex-between mt-8 pt-6 border-t tio-border-default">
              <Button
                @intent="default"
                @appearance="outlined"
                @icon={{PhArrowLeft}}
                @label="Back"
                {{on "click" this.previousStep}}
              />
              <div class="tio-flex">
                {{#if this.needsLrapDiscovery}}
                  <Button
                    @intent="default"
                    @appearance="outlined"
                    @label="Skip for now"
                    {{on "click" this.skipLrapDiscovery}}
                  />
                {{/if}}
                <Button
                  @intent="primary"
                  @icon={{PhArrowRight}}
                  @iconPlacement="end"
                  @label={{if this.hasCompletedLrapDiscovery "Continue" "Save & Continue"}}
                  {{on "click" this.completeLrapDiscovery}}
                />
              </div>
            </div>
          {{/if}}

          {{! ── STEP 5: SET GOAL ── }}
          {{#if (eq this.currentStepId "goals")}}
            {{#if this.hasSetGoal}}
              {{! Completion Screen }}
              <div class="text-center">
                <div
                  class="w-24 h-24 mx-auto mb-6 tio-gradient-success rounded-full tio-row-center"
                >
                  <PhConfetti @size={{48}} class="tio-fg-inverse" @weight="fill" />
                </div>
                <Heading @level={{1}} class="font-bold mb-3">
                  You're All Set!
                </Heading>
                <Text @size="lg" class="tio-fg-secondary max-w-md mx-auto mb-8">
                  Your personalized dashboard is ready. Let's start maximizing your benefits!
                </Text>

                {{! Summary }}
                <div class="tio-bg-sunken rounded-xl p-6 mb-8 text-left max-w-md mx-auto">
                  <Text @weight="medium" class="tio-fg-secondary mb-4">Your Setup Summary</Text>
                  <div class="space-y-3">
                    <div class="tio-row">
                      <PhCheckCircle @size={{20}} class="tio-fg-success" />
                      <Text class="tio-fg-secondary">
                        {{#if this.hasLinkedLoans}}Loans connected{{else}}Loans pending{{/if}}
                      </Text>
                    </div>
                    <div class="tio-row">
                      <PhCheckCircle @size={{20}} class="tio-fg-success" />
                      <Text class="tio-fg-secondary">
                        PSLF eligibility:
                        {{#if this.pslfEligibilityResult.eligible}}Likely eligible{{else}}Exploring
                          other options{{/if}}
                      </Text>
                    </div>
                    {{#if this.showLrapStep}}
                      <div class="tio-row">
                        <PhCheckCircle @size={{20}} class="tio-fg-success" />
                        <Text class="tio-fg-secondary">
                          LRAP programs found:
                          {{this.lrapDiscoveryResult.programsFound}}
                        </Text>
                      </div>
                    {{/if}}
                    <div class="tio-row">
                      <PhCheckCircle @size={{20}} class="tio-fg-success" />
                      <Text class="tio-fg-secondary">
                        Goal:
                        {{this.selectedGoal}}
                      </Text>
                    </div>
                  </div>
                </div>

                <div class="tio-flex-center gap-4">
                  <Button
                    @route="authenticated.reimagined.loans"
                    @intent="primary"
                    @size="lg"
                    @icon={{PhArrowRight}}
                    @iconPlacement="end"
                    @label="Go to Dashboard"
                  />
                </div>
              </div>
            {{else}}
              <div class="mb-8">
                <div class="tio-row mb-2">
                  <PhTarget @size={{28}} class="tio-fg-success" />
                  <Heading @level={{2}} class="font-bold">
                    What's Your Primary Goal?
                  </Heading>
                </div>
                <Text class="tio-fg-secondary">
                  This helps us personalize your dashboard and recommendations.
                </Text>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {{#each this.goalOptions as |goal|}}
                  <button
                    type="button"
                    class="p-5 border-2 rounded-xl text-left transition-all
                      {{if
                        (eq this.selectedGoal goal.id)
                        (if
                          (eq goal.color 'amber')
                          'tio-border-warning tio-bg-warning-soft'
                          (if
                            (eq goal.color 'blue')
                            'tio-border-info tio-bg-info-soft'
                            (if
                              (eq goal.color 'violet')
                              'tio-border-brand tio-bg-brand-wash'
                              'tio-border-success tio-bg-success-soft'
                            )
                          )
                        )
                        'tio-border-default hover:tio-border-strong'
                      }}"
                    {{on "click" (fn this.selectGoal goal.id)}}
                  >
                    <div class="tio-row-top-loose">
                      <div
                        class="p-3 rounded-lg
                          {{if
                            (eq goal.color 'amber')
                            'tio-bg-warning-soft tio-fg-warning'
                            (if
                              (eq goal.color 'blue')
                              'tio-bg-info-soft tio-fg-info'
                              (if
                                (eq goal.color 'violet')
                                'tio-bg-brand-soft tio-fg-brand'
                                'tio-bg-success-soft tio-fg-success'
                              )
                            )
                          }}"
                      >
                        <goal.icon @size={{24}} />
                      </div>
                      <div class="flex-1">
                        <Text @weight="semibold">{{goal.title}}</Text>
                        <Text @size="sm" class="tio-fg-secondary mt-1">{{goal.description}}</Text>
                      </div>
                      {{#if (eq this.selectedGoal goal.id)}}
                        <PhCheckCircle
                          @size={{24}}
                          class={{if
                            (eq goal.color "amber")
                            "tio-fg-warning"
                            (if
                              (eq goal.color "blue")
                              "tio-fg-info"
                              (if (eq goal.color "violet") "tio-fg-brand" "tio-fg-success")
                            )
                          }}
                        />
                      {{/if}}
                    </div>
                  </button>
                {{/each}}
              </div>

              <div class="tio-flex-between mt-8 pt-6 border-t tio-border-default">
                <Button
                  @intent="default"
                  @appearance="outlined"
                  @icon={{PhArrowLeft}}
                  @label="Back"
                  {{on "click" this.previousStep}}
                />
                <Button
                  @intent="primary"
                  @size="lg"
                  @icon={{PhConfetti}}
                  @iconPlacement="end"
                  @label="Complete Setup"
                  disabled={{not this.selectedGoal}}
                  {{on "click" this.completeOnboarding}}
                />
              </div>
            {{/if}}
          {{/if}}
        </Card>
      </VStack>
    </DataIndicator>
  </template>
}
